import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  ShieldCheck,
  Brain,
  Target,
  Boxes,
  Lightbulb,
  Rocket,
  Send,
  ClipboardList,
  PencilRuler,
  FlaskConical,
  TrendingUp,
  CheckCircle2,
  Layers,
  Sparkles,
  Cpu,
  Warehouse,
} from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import SEO from "../components/SEO";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- Design atoms (mirrored from InnovationPage / PlatformsPage) -----------
const Eyebrow = ({ children, tone = "blue", className = "" }) => (
  <p
    className={`text-[11px] font-semibold tracking-[0.22em] uppercase mb-3 ${
      tone === "purple" ? "text-[#7C3AED]" : "text-[#025979]"
    } ${className}`}
  >
    {children}
  </p>
);

const H2 = ({ children, className = "" }) => (
  <h2
    className={`text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-tight text-[#0F172A] leading-[1.1] ${className}`}
    style={{ fontFamily: "Chivo, sans-serif" }}
  >
    {children}
  </h2>
);

// Lightweight on-scroll reveal — subtle fade-up, CSS-only transitions.
const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setShow(true);
          io.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const scrollToForm = (e) => {
  if (e) e.preventDefault();
  const el = document.getElementById("apply-form");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// --- 1. HERO (full-bleed, immersive, right-side artwork) -------------------
const Hero = () => (
  <section
    data-testid="coinnovate-hero"
    className="relative w-full overflow-hidden text-white"
    style={{
      background:
        "linear-gradient(135deg, #0A1330 0%, #0E1A3F 35%, #0B1734 70%, #050B22 100%)",
    }}
  >
    {/* Full-bleed hero artwork — integrated into the canvas exactly like the
        Innovation page. Anchored to the right, object-contain + full height so
        the aspect ratio is preserved and nothing (PRIVA sphere, SAP, PrimeS4 AI,
        Customer card, globe) is ever cropped. The artwork's own dark-navy left
        area, plus a left gradient wash, melt it seamlessly into the background. */}
    <img
      src="/images/coinnovate-hero-v4-950.webp"
      srcSet="/images/coinnovate-hero-v4-640.webp 640w, /images/coinnovate-hero-v4-950.webp 950w"
      sizes="60vw"
      width={950}
      height={584}
      alt="PrimeS4 AI Co-Innovation model — Your Organization, the PRIVA intelligence layer, and SAP co-innovating SAP Business AI for supply chain"
      className="hidden lg:block absolute inset-y-0 right-0 h-full w-auto object-contain object-right select-none pointer-events-none"
      loading="eager"
      fetchpriority="high"
      draggable="false"
      data-testid="coinnovate-hero-image"
    />
    {/* Left-side wash so the copy stays legible and the artwork blends in */}
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "linear-gradient(90deg, rgba(5,11,34,0.96) 0%, rgba(11,23,52,0.82) 32%, rgba(11,23,52,0.4) 55%, rgba(11,23,52,0.05) 80%, rgba(11,23,52,0) 100%)",
      }}
      data-testid="coinnovate-hero-illustration"
    />

    {/* Soft accent glows (preserve the immersive network feel) */}
    <div className="absolute inset-0 pointer-events-none">
      <div
        className="absolute -top-40 -left-32 w-[560px] h-[560px] rounded-full blur-3xl opacity-25"
        style={{ background: "radial-gradient(circle, #1E40AF 0%, transparent 65%)" }}
      />
      <div
        className="absolute top-1/3 -right-20 w-[620px] h-[620px] rounded-full blur-3xl opacity-20"
        style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 65%)" }}
      />
    </div>
    {/* Enterprise grid */}
    <div
      className="absolute inset-0 pointer-events-none opacity-[0.06]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
        backgroundSize: "56px 56px",
        maskImage: "radial-gradient(ellipse at center, black 55%, transparent 90%)",
        WebkitMaskImage: "radial-gradient(ellipse at center, black 55%, transparent 90%)",
      }}
    />
    {/* Bottom hairline */}
    <div
      className="absolute inset-x-0 bottom-0 h-px pointer-events-none z-10"
      style={{
        background:
          "linear-gradient(90deg, transparent 0%, rgba(167,139,250,0.45) 50%, transparent 100%)",
      }}
    />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 lg:min-h-[720px] flex items-center py-16 lg:py-24">
      <Reveal className="w-full lg:max-w-xl">
        <div data-testid="coinnovate-hero-copy">
          <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#A78BFA] mb-6 flex items-center gap-3">
            <span className="inline-block h-px w-8 bg-[#A78BFA]" />
            Co-Innovation
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-[54px] xl:text-[58px] font-bold tracking-tight leading-[1.04]"
            style={{ fontFamily: "Chivo, sans-serif" }}
            data-testid="coinnovate-hero-headline"
          >
            Let&rsquo;s Co-Innovate Together
          </h1>
          <p
            className="mt-5 text-lg sm:text-xl font-medium bg-clip-text text-transparent inline-block"
            style={{ backgroundImage: "linear-gradient(90deg, #5EB8D8 0%, #A78BFA 100%)" }}
            data-testid="coinnovate-hero-subheading"
          >
            Build the Future of SAP Business AI for Supply Chain
          </p>
          <p
            className="mt-6 text-base sm:text-[17px] text-white/80 leading-relaxed max-w-xl"
            data-testid="coinnovate-hero-description"
          >
            Join PrimeS4 AI to co-design, validate and deploy next-generation SAP Business AI
            solutions built for real-world supply chain operations. Collaborate directly with our
            product team to shape WarehouseX, TransportX and future SAP Business AI innovations.
          </p>
          <div className="mt-9 flex flex-wrap gap-3" data-testid="coinnovate-hero-ctas">
            <a
              href="#apply-form"
              onClick={scrollToForm}
              data-testid="coinnovate-hero-cta-apply"
              className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide text-white transition-colors"
              style={{ backgroundColor: "#2563EB" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#1D4ED8")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#2563EB")}
            >
              Apply for Co-Innovation
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
            </a>
            <a
              href="/warehousex"
              data-testid="coinnovate-hero-cta-warehousex"
              className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide border border-white/30 bg-white/5 text-white hover:bg-white/10 hover:border-white/60 transition-colors backdrop-blur-sm"
            >
              Explore WarehouseX
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
            </a>
          </div>
          <div className="mt-10 flex flex-wrap gap-x-7 gap-y-3" data-testid="coinnovate-hero-badges">
            {[
              { icon: ShieldCheck, label: "SAP-Aligned" },
              { icon: Brain, label: "AI-Powered" },
              { icon: Target, label: "Enterprise-Ready" },
            ].map((it) => (
              <div
                key={it.label}
                className="flex items-center gap-2.5 text-[12px] font-semibold tracking-[0.12em] uppercase text-white/85"
                data-testid={`coinnovate-hero-badge-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <it.icon className="w-4 h-4 shrink-0 text-[#A78BFA]" strokeWidth={1.6} />
                <span>{it.label}</span>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </div>

    {/* Mobile-only artwork below text — object-contain, never cropped */}
    <div className="lg:hidden relative px-4 sm:px-6 pb-14 -mt-2">
      <img
        src="/images/coinnovate-hero-v4-640.webp"
        width={950}
        height={584}
        alt="PrimeS4 AI Co-Innovation model — Your Organization, PRIVA intelligence layer, and SAP"
        className="w-full h-auto block object-contain"
        loading="eager"
        draggable="false"
      />
    </div>
  </section>
);

// --- 2. TRUST STRIP --------------------------------------------------------
const TrustStrip = () => {
  const items = [
    { icon: Layers, label: "SAP BTP" },
    { icon: Brain, label: "SAP Business AI" },
    { icon: Sparkles, label: "SAP Joule" },
    { icon: Cpu, label: "PRIVA" },
    { icon: Warehouse, label: "WarehouseX" },
  ];
  return (
    <section
      data-testid="section-trust-strip"
      className="bg-white border-b border-[#E5E7EB]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          <span className="text-[10.5px] font-semibold tracking-[0.2em] uppercase text-[#94A3B8]">
            Built on
          </span>
          {items.map((it) => (
            <div
              key={it.label}
              className="group flex items-center gap-2.5 text-[13px] font-semibold text-[#475569] transition-colors hover:text-[#025979]"
              data-testid={`trust-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <it.icon className="w-4 h-4 text-[#94A3B8] transition-colors group-hover:text-[#025979]" strokeWidth={1.6} />
              <span className="tracking-wide">{it.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- 3. WHY CO-INNOVATE (visual, tighter spacing) --------------------------
const WhyCoInnovate = () => (
  <section data-testid="section-why-coinnovate" className="py-16 lg:py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="text-center max-w-3xl mx-auto">
        <Eyebrow tone="purple" className="!mx-auto">Why Co-Innovate</Eyebrow>
        <H2 className="!mx-auto">Why Co-Innovate With PrimeS4 AI</H2>
      </Reveal>
      <Reveal delay={120} className="mt-12 lg:mt-14 max-w-6xl mx-auto">
        <div
          className="relative rounded-md overflow-hidden bg-[#05070F] border"
          style={{ borderColor: "#E5E7EB", boxShadow: "0 30px 80px rgba(15,23,42,0.10)" }}
          data-testid="why-coinnovate-image-frame"
        >
          <div className="h-1" style={{ background: "linear-gradient(90deg, #5EB8D8 0%, #7C3AED 50%, #5EB8D8 100%)" }} />
          <img
            src="/images/coinnovate-why-1600.webp"
            srcSet="/images/coinnovate-why-1000.webp 1000w, /images/coinnovate-why-1600.webp 1600w"
            sizes="(max-width: 1152px) 100vw, 1152px"
            width={1774}
            height={887}
            alt="Why join PrimeS4 AI Co-Innovation — Innovation, Access, Impact: Co-Design, Early Access, and SAP-Aligned Innovation"
            className="w-full h-auto block"
            loading="lazy"
            data-testid="why-coinnovate-image"
          />
        </div>
      </Reveal>
    </div>
  </section>
);

// --- 4. CURRENT INNOVATION AREAS (visual) ----------------------------------
const CurrentInnovationAreas = () => (
  <section
    data-testid="section-innovation-areas"
    className="py-20 lg:py-28 bg-[#F8FAFC] border-y border-[#E5E7EB]"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="text-center max-w-3xl mx-auto">
        <Eyebrow className="!mx-auto">Innovation Areas</Eyebrow>
        <H2 className="!mx-auto">Current Innovation Areas</H2>
        <p className="mt-5 text-base sm:text-lg text-[#475569] leading-relaxed">
          Built on SAP Foundation. Powered by PRIVA.
        </p>
      </Reveal>
      <Reveal delay={120} className="mt-14 lg:mt-16 max-w-5xl mx-auto">
        <div
          className="relative rounded-md overflow-hidden bg-[#05070F] border"
          style={{ borderColor: "#E5E7EB", boxShadow: "0 30px 80px rgba(15,23,42,0.10)" }}
          data-testid="innovation-areas-image-frame"
        >
          <div className="h-1" style={{ background: "linear-gradient(90deg, #5EB8D8 0%, #7C3AED 50%, #5EB8D8 100%)" }} />
          <img
            src="/images/coinnovate-areas-v3-960.webp"
            srcSet="/images/coinnovate-areas-v3-960.webp 960w"
            sizes="(max-width: 1024px) 100vw, 1024px"
            width={960}
            height={636}
            alt="PrimeS4 AI innovation areas — SAP Foundation to PRIVA intelligence layer powering WarehouseX and TransportX with SmartInspect, SmartEvaluate and SmartRF"
            className="w-full h-auto block"
            loading="lazy"
            data-testid="innovation-areas-image"
          />
        </div>
      </Reveal>
    </div>
  </section>
);

// --- 5. WHO WE'RE LOOKING FOR (centered, premium) --------------------------
const WhoWereLookingFor = () => {
  const cards = [
    { icon: Boxes, title: "SAP Supply Chain", desc: "Organizations running SAP EWM, SAP TM or Digital Supply Chain." },
    { icon: Lightbulb, title: "Innovation Mindset", desc: "Teams interested in solving real operational challenges using AI." },
    { icon: Rocket, title: "Pilot & Scale", desc: "Organizations ready to validate and deploy new capabilities together." },
  ];
  return (
    <section data-testid="section-who-were-looking-for" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center max-w-3xl mx-auto">
          <Eyebrow tone="purple" className="!mx-auto">Who We&rsquo;re Looking For</Eyebrow>
          <H2 className="!mx-auto">The Organizations We Co-Innovate With</H2>
        </Reveal>
        <div
          className="mt-14 lg:mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-9 max-w-5xl mx-auto"
          data-testid="who-cards"
        >
          {cards.map((c, i) => (
            <Reveal key={c.title} delay={i * 90} className="h-full">
              <div
                className="group h-full text-center bg-white border border-[#E5E7EB] rounded-md p-8 lg:p-9 transition-all duration-200 hover:border-[#A78BFA] hover:shadow-lg hover:-translate-y-1"
                data-testid={`who-card-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div
                  className="w-16 h-16 mx-auto rounded-md flex items-center justify-center mb-6"
                  style={{ background: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)" }}
                >
                  <c.icon className="w-7 h-7 text-[#7C3AED]" strokeWidth={1.5} />
                </div>
                <h3 className="text-[18px] font-semibold text-[#0F172A] leading-snug">{c.title}</h3>
                <p className="mt-3 text-[14px] text-[#475569] leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={140}>
          <p
            className="mt-12 text-base sm:text-lg text-[#475569] leading-relaxed max-w-3xl mx-auto text-center"
            data-testid="who-supporting-text"
          >
            We collaborate with a select group of strategic customers each year to build and
            validate next-generation SAP Business AI solutions.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

// --- 6. CO-INNOVATION JOURNEY (continuous glowing line) --------------------
const Journey = () => {
  const steps = [
    { icon: Send, label: "Apply" },
    { icon: ClipboardList, label: "Discovery Workshop" },
    { icon: PencilRuler, label: "Solution Design" },
    { icon: FlaskConical, label: "Pilot" },
    { icon: TrendingUp, label: "Scale Together" },
  ];
  return (
    <section
      data-testid="section-journey"
      className="py-20 lg:py-28 bg-[#F8FAFC] border-y border-[#E5E7EB]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center max-w-3xl mx-auto">
          <Eyebrow className="!mx-auto">The Path</Eyebrow>
          <H2 className="!mx-auto">Co-Innovation Journey</H2>
        </Reveal>
        <Reveal delay={100}>
          <div className="relative mt-14 lg:mt-20" data-testid="journey-steps">
            {/* Continuous glowing connector — horizontal (desktop) */}
            <div
              className="hidden md:block absolute top-8 left-[10%] right-[10%] h-[2px] rounded-full"
              style={{
                background:
                  "linear-gradient(90deg, rgba(94,184,216,0.15) 0%, #5EB8D8 20%, #7C3AED 50%, #5EB8D8 80%, rgba(124,58,237,0.15) 100%)",
                boxShadow: "0 0 14px rgba(124,58,237,0.35)",
              }}
              aria-hidden="true"
            />
            {/* Continuous glowing connector — vertical (mobile) */}
            <div
              className="md:hidden absolute top-8 bottom-8 left-8 w-[2px] rounded-full"
              style={{
                background:
                  "linear-gradient(180deg, #5EB8D8 0%, #7C3AED 100%)",
                boxShadow: "0 0 12px rgba(124,58,237,0.35)",
              }}
              aria-hidden="true"
            />

            <div className="relative flex flex-col md:flex-row md:items-start md:justify-between gap-9 md:gap-4">
              {steps.map((s, i) => (
                <div
                  key={s.label}
                  className="relative flex md:flex-col items-center md:text-center gap-5 md:gap-0 md:flex-1"
                  data-testid={`journey-step-${i + 1}`}
                >
                  <div className="relative z-10 flex items-center justify-center shrink-0">
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center border-2 bg-white transition-transform duration-200 hover:scale-105"
                      style={{ borderColor: "#C7D2FE", boxShadow: "0 10px 28px rgba(37,99,235,0.14)" }}
                    >
                      <s.icon className="w-6 h-6 text-[#2563EB]" strokeWidth={1.6} />
                    </div>
                  </div>
                  <div className="md:mt-5">
                    <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#94A3B8]">
                      Step {i + 1}
                    </p>
                    <p className="mt-1 text-[15px] font-semibold text-[#0F172A] leading-snug">
                      {s.label}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// --- 7. APPLY FORM ---------------------------------------------------------
const EMPTY = {
  company_name: "",
  contact_name: "",
  job_title: "",
  business_email: "",
  country: "",
  industry: "",
  sap_landscape: "",
  business_challenge: "",
  interested_in: [],
  consent: false,
};

const INTEREST_OPTIONS = [
  "SmartInspect",
  "SmartEvaluate",
  "SmartRF",
  "SmartImprove",
  "WarehouseX",
  "TransportX",
  "General AI Strategy",
];

const FormField = ({ label, required, error, children, htmlFor }) => (
  <div className="space-y-1.5">
    <Label htmlFor={htmlFor} className="text-xs font-medium text-[#374151]">
      {label}
      {required && <span className="text-[#DC2626] ml-0.5">*</span>}
    </Label>
    {children}
    {error && (
      <p className="text-[11px] text-[#DC2626]" data-testid={`error-${htmlFor}`}>
        {error}
      </p>
    )}
  </div>
);

const ApplyForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setErrors((p) => ({ ...p, [field]: undefined }));
  };

  const toggleInterest = (opt) => {
    setForm((p) => {
      const has = p.interested_in.includes(opt);
      return {
        ...p,
        interested_in: has ? p.interested_in.filter((x) => x !== opt) : [...p.interested_in, opt],
      };
    });
  };

  const validate = () => {
    const e = {};
    if (!form.company_name.trim()) e.company_name = "Company name is required.";
    if (!form.contact_name.trim()) e.contact_name = "Contact name is required.";
    if (!form.job_title.trim()) e.job_title = "Job title is required.";
    if (!form.business_email.trim()) e.business_email = "Business email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.business_email.trim()))
      e.business_email = "Enter a valid email address.";
    if (!form.country.trim()) e.country = "Country is required.";
    if (!form.consent) e.consent = "Please provide consent to be contacted.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    if (!validate()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/coinnovation-applications`, {
        ...form,
        business_email: form.business_email.trim().toLowerCase(),
        source_path: typeof window !== "undefined" ? window.location.pathname : "",
      });
      setSubmitted(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't submit your application. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="apply-form" data-testid="section-apply" className="py-20 lg:py-28 bg-white scroll-mt-20">
      <div className="max-w-[860px] mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="relative rounded-md bg-white border p-6 sm:p-9 lg:p-12"
          style={{ borderColor: "#E5E7EB", boxShadow: "0 30px 80px rgba(15,23,42,0.08)" }}
        >
          <div
            className="absolute inset-x-0 top-0 h-1 rounded-t-md"
            style={{ background: "linear-gradient(90deg, #5EB8D8 0%, #7C3AED 50%, #5EB8D8 100%)" }}
          />

          {submitted ? (
            <div className="text-center py-8" data-testid="apply-success">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center bg-[#ECFDF5] mb-5">
                <CheckCircle2 className="w-7 h-7 text-[#059669]" strokeWidth={1.8} />
              </div>
              <h3
                className="text-2xl font-bold text-[#0F172A] tracking-tight"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                Thank You
              </h3>
              <p className="mt-3 text-base text-[#475569] leading-relaxed max-w-md mx-auto">
                Thank you for your interest in partnering with PrimeS4 AI. Our team will review your
                application and contact you shortly.
              </p>
            </div>
          ) : (
            <>
              <Eyebrow tone="purple">Apply</Eyebrow>
              <H2>Apply for Co-Innovation</H2>
              <p className="mt-4 text-base text-[#475569] leading-relaxed">
                Tell us about your organization and we&rsquo;ll contact you to discuss the
                opportunity.
              </p>

              <form onSubmit={onSubmit} className="mt-8 space-y-6" noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField label="Company Name" required htmlFor="company_name" error={errors.company_name}>
                    <Input id="company_name" data-testid="field-company-name" value={form.company_name} onChange={(e) => update("company_name", e.target.value)} className="rounded-sm" />
                  </FormField>
                  <FormField label="Contact Name" required htmlFor="contact_name" error={errors.contact_name}>
                    <Input id="contact_name" data-testid="field-contact-name" value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} className="rounded-sm" />
                  </FormField>
                  <FormField label="Job Title" required htmlFor="job_title" error={errors.job_title}>
                    <Input id="job_title" data-testid="field-job-title" value={form.job_title} onChange={(e) => update("job_title", e.target.value)} className="rounded-sm" />
                  </FormField>
                  <FormField label="Business Email" required htmlFor="business_email" error={errors.business_email}>
                    <Input id="business_email" type="email" data-testid="field-business-email" value={form.business_email} onChange={(e) => update("business_email", e.target.value)} className="rounded-sm" />
                  </FormField>
                  <FormField label="Country" required htmlFor="country" error={errors.country}>
                    <Input id="country" data-testid="field-country" value={form.country} onChange={(e) => update("country", e.target.value)} className="rounded-sm" />
                  </FormField>
                  <FormField label="Industry" htmlFor="industry">
                    <Input id="industry" data-testid="field-industry" value={form.industry} onChange={(e) => update("industry", e.target.value)} className="rounded-sm" />
                  </FormField>
                </div>

                <FormField label="SAP Landscape" htmlFor="sap_landscape">
                  <Select value={form.sap_landscape} onValueChange={(v) => update("sap_landscape", v)}>
                    <SelectTrigger id="sap_landscape" data-testid="field-sap-landscape" className="rounded-sm">
                      <SelectValue placeholder="Select your SAP landscape" />
                    </SelectTrigger>
                    <SelectContent>
                      {["SAP EWM", "SAP TM", "SAP S/4HANA", "SAP Business Network", "Other"].map((o) => (
                        <SelectItem key={o} value={o} data-testid={`sap-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                          {o}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>

                <FormField label="Current Business Challenge" htmlFor="business_challenge">
                  <Textarea
                    id="business_challenge"
                    data-testid="field-business-challenge"
                    value={form.business_challenge}
                    onChange={(e) => update("business_challenge", e.target.value)}
                    className="rounded-sm min-h-[96px]"
                    placeholder="Briefly describe the operational challenge you'd like to solve."
                  />
                </FormField>

                <div className="space-y-2.5">
                  <Label className="text-xs font-medium text-[#374151]">Interested In</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5" data-testid="field-interested-in">
                    {INTEREST_OPTIONS.map((opt) => {
                      const id = `interest-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
                      return (
                        <label key={opt} htmlFor={id} className="flex items-center gap-2.5 cursor-pointer text-[13.5px] text-[#334155]">
                          <Checkbox id={id} data-testid={id} checked={form.interested_in.includes(opt)} onCheckedChange={() => toggleInterest(opt)} />
                          <span>{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="pt-1">
                  <label htmlFor="consent" className="flex items-start gap-3 cursor-pointer">
                    <Checkbox id="consent" data-testid="field-consent" checked={form.consent} onCheckedChange={(v) => update("consent", v === true)} className="mt-0.5" />
                    <span className="text-[13px] text-[#475569] leading-relaxed">
                      I agree to be contacted by PrimeS4 AI regarding the Co-Innovation Program.
                    </span>
                  </label>
                  {errors.consent && (
                    <p className="mt-1.5 text-[11px] text-[#DC2626]" data-testid="error-consent">
                      {errors.consent}
                    </p>
                  )}
                </div>

                <div className="pt-3 border-t border-[#E5E7EB]">
                  <button
                    type="submit"
                    data-testid="apply-submit-btn"
                    disabled={submitting}
                    className="group w-full inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-sm font-semibold tracking-wide text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ backgroundColor: "#025979" }}
                    onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.backgroundColor = "#03738C"; }}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#025979")}
                  >
                    {submitting ? (
                      <>
                        <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                        Submitting&hellip;
                      </>
                    ) : (
                      <>
                        Apply for Co-Innovation
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

// --- 8. FINAL CTA (larger, more impactful) ---------------------------------
const FinalCta = () => (
  <section
    data-testid="section-final-cta"
    className="relative py-28 lg:py-36 text-white overflow-hidden"
    style={{ background: "linear-gradient(135deg, #0A1330 0%, #0E1A3F 50%, #0B1734 100%)" }}
  >
    <div className="absolute inset-0 pointer-events-none">
      <div
        className="absolute top-0 left-1/3 w-[480px] h-[480px] rounded-full blur-3xl opacity-25"
        style={{ background: "radial-gradient(circle, #025979 0%, transparent 60%)" }}
      />
      <div
        className="absolute bottom-0 right-1/4 w-[420px] h-[420px] rounded-full blur-3xl opacity-20"
        style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 60%)" }}
      />
    </div>
    <div
      className="absolute inset-0 pointer-events-none opacity-[0.05]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
        backgroundSize: "56px 56px",
        maskImage: "radial-gradient(ellipse at center, black 60%, transparent 95%)",
        WebkitMaskImage: "radial-gradient(ellipse at center, black 60%, transparent 95%)",
      }}
    />
    <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#A78BFA] mb-6 flex items-center justify-center gap-3">
        <span className="inline-block h-px w-8 bg-[#A78BFA]" />
        Co-Innovation Program
        <span className="inline-block h-px w-8 bg-[#A78BFA]" />
      </p>
      <h2
        className="text-4xl sm:text-5xl lg:text-[52px] font-bold tracking-tight leading-[1.08]"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        Ready to Build the Future of{" "}
        <span
          className="bg-clip-text text-transparent"
          style={{ backgroundImage: "linear-gradient(90deg, #5EB8D8 0%, #A78BFA 100%)" }}
        >
          SAP Business AI for Supply Chain?
        </span>
      </h2>
      <div className="mt-12 flex justify-center">
        <a
          href="#apply-form"
          onClick={scrollToForm}
          data-testid="final-cta-apply"
          className="group inline-flex items-center justify-center gap-2 rounded-sm h-14 px-10 text-base font-semibold tracking-wide text-white transition-all duration-200 hover:-translate-y-0.5"
          style={{ backgroundColor: "#2563EB", boxShadow: "0 16px 40px -12px rgba(37,99,235,0.7)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#1D4ED8")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#2563EB")}
        >
          Apply Now
          <ArrowRight className="w-4.5 h-4.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
        </a>
      </div>
    </div>
  </section>
);

// --- Page ------------------------------------------------------------------
const CoInnovatePage = () => (
  <div className="min-h-screen flex flex-col bg-white" data-testid="coinnovate-page">
    <SEO
      title="Co-Innovate with PrimeS4 AI | SAP Supply Chain AI"
      description="Partner with PrimeS4 AI to co-design, validate and deploy next-generation SAP Supply Chain AI solutions built on SAP Business AI, SAP Joule and SAP BTP."
      keywords={[
        "Co-Innovation",
        "SAP Supply Chain AI",
        "SAP Business AI",
        "SAP Joule",
        "SAP BTP",
        "PrimeS4 AI",
        "WarehouseX",
        "TransportX",
      ]}
      canonical="/coinnovate"
      imageAlt="Co-Innovate with PrimeS4 AI — build the future of SAP Business AI for supply chain"
    />
    <Header />
    <main className="flex-1">
      <Hero />
      <TrustStrip />
      <WhyCoInnovate />
      <CurrentInnovationAreas />
      <WhoWereLookingFor />
      <Journey />
      <ApplyForm />
      <FinalCta />
    </main>
    <Footer hideSapBadge />
  </div>
);

export default CoInnovatePage;
